import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Fuel, CreditCard } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1765521917320-c011148e65e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjBuZW9uJTIwZ3JlZW4lMjBsaWdodHN8ZW58MHx8fHwxNzg4ODg0ODAwfDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const GaugeArc = () => (
  <motion.svg
    viewBox="0 0 200 110"
    className="absolute -top-10 right-0 w-24 sm:-top-6 sm:right-0 sm:w-56 opacity-40 sm:opacity-60"
    initial={{ opacity: 0 }}
    animate={{ opacity: 0.6 }}
    transition={{ delay: 1.4, duration: 1 }}
    data-testid="hero-gauge-arc"
  >
    <path d="M 15 100 A 85 85 0 0 1 185 100" fill="none" stroke="#454C50" strokeWidth="10" strokeLinecap="round" />
    <motion.path
      d="M 15 100 A 85 85 0 0 1 185 100"
      fill="none"
      stroke="#6EB000"
      strokeWidth="10"
      strokeLinecap="round"
      strokeDasharray="267"
      initial={{ strokeDashoffset: 267 }}
      animate={{ strokeDashoffset: 80 }}
      transition={{ delay: 1.2, duration: 1.6, ease: "easeOut" }}
    />
    <motion.line
      x1="100" y1="100" x2="160" y2="55"
      stroke="#F5A623" strokeWidth="3" strokeLinecap="round"
      initial={{ rotate: -120, opacity: 0 }}
      animate={{ rotate: 0, opacity: 1 }}
      style={{ transformOrigin: "100px 100px" }}
      transition={{ delay: 1.5, duration: 1.4, ease: [0.34, 1.56, 0.64, 1] }}
    />
    <circle cx="100" cy="100" r="6" fill="#F5A623" />
  </motion.svg>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="accueil" ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-[#272B2E]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
      </motion.div>

      <motion.div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 w-full" style={{ opacity: fade }}>
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-script text-2xl sm:text-3xl text-[#6EB000] mb-4"
          data-testid="hero-script-tagline"
        >
          ★ Passionné depuis 1998
        </motion.p>

        <div className="relative">
          <GaugeArc />
          <h1 className="font-display leading-[0.95] text-[11vw] sm:text-[9.5vw] lg:text-[7.2rem]" data-testid="hero-title">
            <Line delay={0.3}>
              <span className="text-white">Reprogrammation moteur</span>
            </Line>
            <Line delay={0.45}>
              <span className="text-[#6EB000]">essence</span>
            </Line>
            <Line delay={0.58}>
              <span className="font-script italic font-medium normal-case text-[#4DA3FF] text-[0.72em] tracking-normal">
                super éthanol E85
              </span>
            </Line>
            <Line delay={0.68}>
              <span className="text-[#B45309]">diesel</span>
            </Line>
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 max-w-xl"
        >
          <p className="text-white/70 text-base sm:text-lg" data-testid="hero-subtitle">
            Diagnostic électronique <span className="text-[#6EB000]">•</span> Cartographie sur mesure réalisée par un professionnel
          </p>
          <p className="font-script text-xl sm:text-2xl text-[#6EB000] mt-3" data-testid="hero-script-note">
            Étude sérieuse selon votre véhicule
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-quote"
            className="clip-angle inline-flex items-center gap-2 bg-[#6EB000] text-black font-bold px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#5A9000] active:scale-95 transition-all duration-200"
          >
            <Fuel className="w-4 h-4" />
            Demander une étude
          </a>
          <a
            href="#services"
            data-testid="hero-cta-services"
            className="clip-angle inline-flex items-center gap-2 border border-[#6EB000] text-[#6EB000] px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#6EB000]/10 active:scale-95 transition-all duration-200"
          >
            Découvrir les services
            <ArrowDown className="w-4 h-4" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-6 max-w-xl border border-[#C2703D]/70 bg-[#F5A623]/5 backdrop-blur-sm p-5"
          data-testid="hero-installments"
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-[#F5A623] shrink-0" strokeWidth={1.8} />
            <p className="font-display text-lg sm:text-xl text-white tracking-wide">
              Paiement en plusieurs fois <span className="text-[#F5A623]">disponible</span>
            </p>
          </div>
          <p className="text-sm sm:text-base text-white/80 mt-3 leading-relaxed">
            <span className="text-white font-bold">4x sans frais</span> · 6x, 12x ou 24x avec intérêts faibles
          </p>
          <p className="text-xs sm:text-sm text-white/50 mt-2">
            Disponible à partir de 120 € d'achat, selon éligibilité et conditions du prestataire de paiement.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0, rotate: -30 }}
          animate={{ opacity: 1, scale: 1, rotate: -8 }}
          transition={{ delay: 1.6, type: "spring", stiffness: 120 }}
          className="absolute right-2 bottom-0 sm:right-8 sm:bottom-4 w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-[#F5A623] flex flex-col items-center justify-center text-black shadow-[0_0_50px_rgba(245,166,35,0.35)]"
          data-testid="hero-price-badge"
        >
          <span className="font-display text-lg sm:text-2xl leading-none">Prix</span>
          <span className="font-display text-sm sm:text-lg leading-none mt-1">imbattable</span>
          <span className="text-[9px] sm:text-[10px] mt-1 tracking-wider">★★★ ★★★</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}
